import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import PaymentGatewaySelector from '../components/PaymentGatewaySelector';
import CategorySpecificBooking from '../components/CategorySpecificBooking';
import PhonePePaymentModalEnhanced from '../components/PhonePePaymentModalEnhanced';

const PassTypeModal = ({ isOpen, onClose, onSelect }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold text-center mb-6 text-green-800">🎫 Pass Ticket Type</h3>
        
        <div className="space-y-4">
          <button
            onClick={() => onSelect('new')}
            className="w-full p-4 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            🆕 New Pass Ticket
            <div className="text-sm opacity-90 mt-1">Apply for a new monthly pass</div>
          </button>
          
          <button
            onClick={() => onSelect('renew')}
            className="w-full p-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            🔄 Renew Existing Pass
            <div className="text-sm opacity-90 mt-1">Renew your current pass</div>
          </button>
        </div>
        
        <button
          onClick={onClose}
          className="w-full mt-4 p-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const CompactBooking = ({ selectedTicket, onBack, onBookingSubmit }) => {
  const [bookingForm, setBookingForm] = useState({
    adults: 1,
    children: 0,
    quantity: 1,
    visitDate: '',
    customerDetails: { name: '', email: '', phone: '' }
  });
  const [categoryData, setCategoryData] = useState({});
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [siteInfo, setSiteInfo] = useState(null);
  const [renewalData, setRenewalData] = useState(null);
  const [isRenewal, setIsRenewal] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [passType, setPassType] = useState(null);
  const [slotAvailability, setSlotAvailability] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [showPhonePeModal, setShowPhonePeModal] = useState(false);
  const [phonePeData, setPhonePeData] = useState(null);

  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setBookingForm(prev => ({
      ...prev,
      visitDate: tomorrow.toISOString().split('T')[0]
    }));
    
    // Set default pass type and pricing for pass tickets
    if (selectedTicket?.category === 'pass_ticket' && !passType) {
      setPassType('new');
      // Set default to monthly if available
      if (selectedTicket?.passTicketSettings?.passPricing?.monthly?.price > 0) {
        setCategoryData(prev => ({
          ...prev,
          selectedPassPeriod: 'monthly',
          passPeriodPrice: selectedTicket.passTicketSettings.passPricing.monthly.price,
          validityDays: selectedTicket.passTicketSettings.passPricing.monthly.validityDays
        }));
      }
    }
    
    // Fetch site info for child age definition
    if (selectedTicket) {
      fetchSiteInfo();
    }
  }, [selectedTicket]);

  useEffect(() => {
    if (selectedTicket?.category === 'pass_ticket' && selectedTicket?.passTicketSettings?.slotManagement?.enabled && bookingForm.visitDate) {
      fetchSlotAvailability(bookingForm.visitDate);
    }
  }, [bookingForm.visitDate, selectedTicket]);

  const handlePassTypeSelect = (type) => {
    setPassType(type);
    setIsRenewal(type === 'renew');
    setShowPassModal(false);
  };
  
  const fetchSiteInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/super-admin/sites`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || 'dummy'}` }
      }).catch(() => ({ data: { sites: [] } }));
      
      const site = response.data.sites?.find(s => 
        s.name.toLowerCase() === selectedTicket.name.toLowerCase() && 
        s.city.toLowerCase() === selectedTicket.city.toLowerCase()
      );
      setSiteInfo(site);
    } catch (error) {
      console.error('Error fetching site info:', error);
    }
  };

  const fetchSlotAvailability = async (date) => {
    if (!selectedTicket?._id || !date) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/slots/availability/${selectedTicket._id}?date=${date}`);
      setSlotAvailability(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching slot availability:', error);
      setSlotAvailability([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedGateway) {
      alert('Please select a payment gateway');
      return;
    }
    
    try {
      let totalAmount, totalQuantity;
      
      if (selectedTicket.category === 'entry_ticket') {
        const adultPrice = selectedTicket.price;
        const childPrice = selectedTicket.childPrice || Math.round(selectedTicket.price * 0.7);
        totalAmount = (adultPrice * bookingForm.adults) + (childPrice * bookingForm.children);
        totalQuantity = bookingForm.adults + bookingForm.children;
      } else {
        if (selectedTicket.category === 'parking_ticket' && categoryData.totalAmount) {
          totalAmount = categoryData.totalAmount;
        } else if (selectedTicket.category === 'pass_ticket' && categoryData.passPeriodPrice) {
          totalAmount = categoryData.passPeriodPrice;
        } else {
          totalAmount = selectedTicket.price * bookingForm.quantity;
        }
        totalQuantity = bookingForm.quantity;
      }
      
      const bookingData = {
        ticketTypeId: selectedTicket._id,
        quantity: totalQuantity,
        adults: selectedTicket.category === 'entry_ticket' ? bookingForm.adults : 0,
        children: selectedTicket.category === 'entry_ticket' ? bookingForm.children : 0,
        visitDate: bookingForm.visitDate,
        customerName: bookingForm.customerDetails.name,
        customerEmail: bookingForm.customerDetails.email,
        customerPhone: bookingForm.customerDetails.phone,
        totalAmount: ['puja_ticket', 'parking_ticket', 'function_hall_ticket'].includes(selectedTicket.category) ? (categoryData.totalAmount || totalAmount) : totalAmount,
        gatewayId: selectedGateway._id,
        categorySpecificData: categoryData,
        documents: selectedTicket.category === 'pass_ticket' ? {
          passportPhoto: categoryData.passportPhoto,
          idProof: categoryData.idProof
        } : undefined,
        additionalInfo: selectedTicket.category === 'pass_ticket' ? 
          Object.keys(categoryData)
            .filter(key => key.startsWith('field_'))
            .reduce((acc, key) => {
              acc[key] = categoryData[key];
              return acc;
            }, {}) : undefined,
        selectedSlot: selectedTicket.category === 'pass_ticket' && categoryData.selectedSlot ? {
          startTime: categoryData.slotStartTime,
          endTime: categoryData.slotEndTime
        } : undefined,
        isRenewal: selectedTicket.category === 'pass_ticket' ? isRenewal : false
      };

      const response = await axios.post(`${API_BASE_URL}/api/test-bookings/create`, bookingData);
      
      if (response.data.success && response.data.paymentUrl) {
        console.log('[CompactBooking] Payment URL received:', response.data.paymentUrl);
        console.log('[CompactBooking] Booking created:', response.data.booking?.bookingId);
        
        // Store booking ID in session storage for payment status page
        if (response.data.booking?.bookingId) {
          sessionStorage.setItem('lastBookingId', response.data.booking.bookingId);
          console.log('[CompactBooking] Stored booking ID in session storage');
        }
        
        // Check if this is a PhonePe URL (contains phonepe.com or mercury.phonepe.com)
        const isPhonePeUrl = response.data.paymentUrl.includes('phonepe.com');
        
        if (isPhonePeUrl) {
          // TODO: Enable modal for production
          // For sandbox, use simple redirect to see PhonePe page
          // For production, use modal with polling for better UX
          
          const USE_MODAL = false; // Set to true for production
          
          if (USE_MODAL) {
            // Production: Open PhonePe in modal with countdown and webhook listening
            console.log('[CompactBooking] Opening PhonePe modal:', response.data.paymentUrl);
            console.log('[CompactBooking] Booking ID:', response.data.booking.bookingId);
            setPhonePeData({
              checkoutUrl: response.data.paymentUrl,
              orderId: response.data.booking.bookingId,
              bookingId: response.data.booking.bookingId
            });
            setShowPhonePeModal(true);
          } else {
            // Sandbox: Simple redirect to see PhonePe page and callback flow
            console.log('[CompactBooking] Redirecting to PhonePe (sandbox mode):', response.data.paymentUrl);
            window.location.href = response.data.paymentUrl;
          }
        } else if (response.data.paymentUrl.startsWith('PHONEPE_INITIATE:')) {
          // Legacy PhonePe initiation flow (kept for backward compatibility)
          const gatewayId = response.data.paymentUrl.split(':')[1];
          const phonePeResponse = await axios.post(`${API_BASE_URL}/api/phonepe-initiate`, {
            gatewayId,
            paymentData: {
              orderId: bookingData.customerName.replace(/\s+/g, '') + Date.now(),
              amount: totalAmount,
              customerName: bookingData.customerName,
              customerEmail: bookingData.customerEmail,
              customerPhone: bookingData.customerPhone
            }
          });
          
          console.log('[CompactBooking] PhonePe Response:', phonePeResponse.data);
          
          if (phonePeResponse.data.success && phonePeResponse.data.redirectUrl) {
            console.log('[CompactBooking] Redirecting to:', phonePeResponse.data.redirectUrl);
            window.location.href = phonePeResponse.data.redirectUrl;
          } else if (phonePeResponse.data.phonepeResponse && phonePeResponse.data.phonepeResponse.redirectUrl) {
            console.log('[CompactBooking] Redirecting to (nested):', phonePeResponse.data.phonepeResponse.redirectUrl);
            window.location.href = phonePeResponse.data.phonepeResponse.redirectUrl;
          } else {
            console.error('[CompactBooking] No redirect URL found in response:', phonePeResponse.data);
            alert('PhonePe initialization failed: No redirect URL received');
          }
        } else {
          // Other payment gateways - open in new window
          console.log('[CompactBooking] Opening payment in new window');
          const paymentWindow = window.open(response.data.paymentUrl, '_blank', 'width=1000,height=700');
          if (!paymentWindow) {
            window.location.href = response.data.paymentUrl;
          }
        }
      } else if (response.data.success && response.data.booking) {
        // Booking created but no payment URL (e.g., auto-confirmed in dev mode)
        console.log('[CompactBooking] Booking confirmed without payment:', response.data.booking.bookingId);
        alert(`Booking confirmed! Booking ID: ${response.data.booking.bookingId}`);
        if (onBookingSubmit) {
          onBookingSubmit(response.data.booking);
        }
      }
    } catch (error) {
      alert('Booking failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const hasChildPricing = selectedTicket?.category === 'entry_ticket' && 
    (selectedTicket.childPrice !== selectedTicket.price && selectedTicket.childPrice !== undefined);
  
  const totalAmount = selectedTicket ? 
    selectedTicket.category === 'entry_ticket' ?
      (selectedTicket.price * bookingForm.adults) + ((selectedTicket.childPrice || Math.round(selectedTicket.price * 0.7)) * bookingForm.children) :
    selectedTicket.category === 'pass_ticket' ?
      categoryData.passPeriodPrice || 0 :
    ['puja_ticket', 'parking_ticket', 'function_hall_ticket'].includes(selectedTicket.category) ?
      categoryData.totalAmount || 0 :
      selectedTicket.price * bookingForm.quantity
    : 0;

  return (
    <div style={{ 
      maxWidth: '800px',
      margin: '0 auto',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
      padding: '32px', 
      borderRadius: '24px',
      boxShadow: '0 25px 50px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.2)',
      border: '1px solid rgba(255,255,255,0.3)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23f1f5f9" fill-opacity="0.4"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        pointerEvents: 'none',
        zIndex: 0
      }} />
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        marginBottom: '32px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '64px', 
            height: '64px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: '1.8rem',
            boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
            border: '2px solid rgba(255,255,255,0.2)'
          }}>🎫</div>
          <div>
            <h3 style={{ 
              margin: 0, 
              fontSize: '1.5rem', 
              fontWeight: '800', 
              color: '#1a202c',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {selectedTicket?.name}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
              <span style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)', 
                color: 'white',
                padding: '4px 12px', 
                borderRadius: '20px', 
                fontSize: '11px', 
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
              }}>
                {selectedTicket?.category === 'pass_ticket' ? 
                  `${selectedTicket?.validityDays || 30} Day Pass` : 
                  selectedTicket?.category?.replace('_', ' ')}
              </span>
              <span style={{
                background: 'rgba(16, 185, 129, 0.1)',
                color: '#059669',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '10px',
                fontWeight: '600'
              }}>✨ Instant Booking</span>
            </div>
          </div>
        </div>
        <button onClick={onBack} style={{ 
          background: 'rgba(255,255,255,0.9)', 
          color: '#64748b', 
          border: '1px solid rgba(226, 232, 240, 0.8)',
          padding: '10px 16px', 
          borderRadius: '12px', 
          cursor: 'pointer', 
          fontSize: '13px',
          fontWeight: '600',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease'
        }}
        onMouseOver={(e) => {
          e.target.style.background = 'rgba(248, 250, 252, 0.95)';
          e.target.style.transform = 'translateY(-1px)';
          e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
        }}
        onMouseOut={(e) => {
          e.target.style.background = 'rgba(255,255,255,0.9)';
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
        }}>← Back</button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Child Age Definition for Entry Tickets */}
        {selectedTicket?.category === 'entry_ticket' && hasChildPricing && siteInfo && (
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '12px', color: '#0369a1', fontWeight: '600' }}>
              📝 Child Definition: Children below {siteInfo.childAgeLimit || 12} years are considered as child
            </div>
          </div>
        )}

        {/* Customer Details Section */}
        <div style={{
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid rgba(255,255,255,0.3)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
          position: 'relative',
          zIndex: 1
        }}>
          <h4 style={{
            margin: '0 0 16px 0',
            fontSize: '16px',
            fontWeight: '700',
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            👤 Customer Details
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <input type="tel" placeholder="Phone Number *" required
            value={bookingForm.customerDetails.phone}
            onChange={(e) => {
              const phone = e.target.value.replace(/[^0-9]/g, '');
              if (phone.length <= 10) {
                setBookingForm({...bookingForm, customerDetails: {...bookingForm.customerDetails, phone}});
              }
            }}
            onBlur={async (e) => {
              const phone = e.target.value.replace(/[^0-9]/g, '');
              if (selectedTicket?.category === 'pass_ticket' && phone.length === 10) {
                try {
                  const response = await axios.post(`${API_BASE_URL}/api/test-bookings/check-renewal`, {
                    phone,
                    ticketTypeId: selectedTicket._id
                  });
                  
                  if (response.data.canRenew) {
                    setRenewalData(response.data.existingPass);
                    setIsRenewal(true);
                    setPassType('renew');
                    
                    // Set start date to next day of active subscription
                    const nextDay = new Date(response.data.existingPass.validTill);
                    nextDay.setDate(nextDay.getDate() + 1);
                    
                    setBookingForm(prev => ({
                      ...prev,
                      visitDate: nextDay.toISOString().split('T')[0],
                      customerDetails: {
                        ...prev.customerDetails,
                        name: response.data.existingPass.customerDetails.name,
                        email: response.data.existingPass.customerDetails.email
                      }
                    }));
                    setCategoryData(response.data.existingPass.additionalInfo || {});
                  } else {
                    setRenewalData(null);
                    setIsRenewal(false);
                    setPassType('new');
                  }
                } catch (error) {
                  console.error('Renewal check failed:', error);
                  setPassType('new');
                }
              } else if (selectedTicket?.category === 'pass_ticket') {
                setPassType('new');
              }
            }}
            style={{ 
              padding: '14px 16px', 
              border: '2px solid rgba(226, 232, 240, 0.6)', 
              borderRadius: '12px', 
              fontSize: '14px',
              background: 'rgba(255,255,255,0.8)',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              e.target.style.background = 'white';
            }}
          />
          <input type="text" placeholder="Full Name *" required
            value={bookingForm.customerDetails.name}
            onChange={(e) => setBookingForm({...bookingForm, customerDetails: {...bookingForm.customerDetails, name: e.target.value}})}
            style={{ 
              padding: '14px 16px', 
              border: '2px solid rgba(226, 232, 240, 0.6)', 
              borderRadius: '12px', 
              fontSize: '14px',
              background: 'rgba(255,255,255,0.8)',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              e.target.style.background = 'white';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(226, 232, 240, 0.6)';
              e.target.style.boxShadow = 'none';
              e.target.style.background = 'rgba(255,255,255,0.8)';
            }}
          />
        </div>
          <input 
            type="email" 
            placeholder={selectedTicket?.category === 'pass_ticket' ? 'Email *' : 'Email (Optional)'}
            value={bookingForm.customerDetails.email}
            onChange={(e) => setBookingForm({...bookingForm, customerDetails: {...bookingForm.customerDetails, email: e.target.value}})}
            required={selectedTicket?.category === 'pass_ticket'}
            style={{ 
              width: '100%', 
              padding: '14px 16px', 
              border: '2px solid rgba(226, 232, 240, 0.6)', 
              borderRadius: '12px', 
              fontSize: '14px',
              background: 'rgba(255,255,255,0.8)',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              e.target.style.background = 'white';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(226, 232, 240, 0.6)';
              e.target.style.boxShadow = 'none';
              e.target.style.background = 'rgba(255,255,255,0.8)';
            }}
          />
        </div>

        {/* Booking Details Section */}
        <div style={{
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid rgba(255,255,255,0.3)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
          position: 'relative',
          zIndex: 1
        }}>
          <h4 style={{
            margin: '0 0 16px 0',
            fontSize: '16px',
            fontWeight: '700',
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🎯 Booking Details
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '20px' }}>
          {selectedTicket?.category === 'entry_ticket' ? (
            <>
              <div>
                <label style={{ 
                  fontSize: '13px', 
                  fontWeight: '700', 
                  color: '#4a5568', 
                  marginBottom: '8px', 
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {hasChildPricing ? '👨👩👧👦 Adults' : '👥 People'}
                </label>
                <input type="number" min="1" max="10" value={bookingForm.adults}
                  onChange={(e) => setBookingForm({...bookingForm, adults: parseInt(e.target.value)})}
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    border: '2px solid rgba(226, 232, 240, 0.6)', 
                    borderRadius: '12px', 
                    fontSize: '15px',
                    fontWeight: '600',
                    background: 'rgba(255,255,255,0.9)',
                    transition: 'all 0.2s ease',
                    textAlign: 'center'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(226, 232, 240, 0.6)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div style={{ 
                  fontSize: '12px', 
                  color: '#667eea', 
                  marginTop: '6px',
                  fontWeight: '600',
                  textAlign: 'center',
                  background: 'rgba(102, 126, 234, 0.1)',
                  padding: '4px 8px',
                  borderRadius: '8px'
                }}>₹{selectedTicket?.price} each</div>
              </div>
              {hasChildPricing && (
                <div>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: '700', 
                    color: '#4a5568', 
                    marginBottom: '8px', 
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    👶 Children (below {siteInfo?.childAgeLimit || 12})
                  </label>
                  <input type="number" min="0" max="10" value={bookingForm.children}
                    onChange={(e) => setBookingForm({...bookingForm, children: parseInt(e.target.value)})}
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      border: '2px solid rgba(226, 232, 240, 0.6)', 
                      borderRadius: '12px', 
                      fontSize: '15px',
                      fontWeight: '600',
                      background: 'rgba(255,255,255,0.9)',
                      transition: 'all 0.2s ease',
                      textAlign: 'center'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(226, 232, 240, 0.6)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#10b981', 
                    marginTop: '6px',
                    fontWeight: '600',
                    textAlign: 'center',
                    background: 'rgba(16, 185, 129, 0.1)',
                    padding: '4px 8px',
                    borderRadius: '8px'
                  }}>
                    ₹{selectedTicket?.childPrice || Math.round(selectedTicket?.price * 0.7)} each
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>
              <label style={{ 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#4a5568', 
                marginBottom: '8px', 
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>🎫 Quantity</label>
              <input type="number" min="1" max="10" value={bookingForm.quantity}
                onChange={(e) => setBookingForm({...bookingForm, quantity: parseInt(e.target.value)})}
                disabled={selectedTicket?.category === 'function_hall_ticket'}
                style={{ 
                  width: '100%', 
                  padding: '12px 16px', 
                  border: '2px solid rgba(226, 232, 240, 0.6)', 
                  borderRadius: '12px', 
                  fontSize: '15px',
                  fontWeight: '600',
                  background: selectedTicket?.category === 'function_hall_ticket' ? 'rgba(248, 250, 252, 0.8)' : 'rgba(255,255,255,0.9)',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  opacity: selectedTicket?.category === 'function_hall_ticket' ? 0.6 : 1
                }}
                onFocus={(e) => {
                  if (selectedTicket?.category !== 'function_hall_ticket') {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(226, 232, 240, 0.6)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <div style={{ 
                fontSize: '12px', 
                color: '#667eea', 
                marginTop: '6px',
                fontWeight: '600',
                textAlign: 'center',
                background: 'rgba(102, 126, 234, 0.1)',
                padding: '4px 8px',
                borderRadius: '8px'
              }}>₹{selectedTicket?.price} each</div>
            </div>
          )}
          <div>
            <label style={{ 
              fontSize: '13px', 
              fontWeight: '700', 
              color: '#4a5568', 
              marginBottom: '8px', 
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {selectedTicket?.category === 'pass_ticket' ? '📅 Pass Starts From' : '📅 Visit Date'}
            </label>
            <input type="date" value={bookingForm.visitDate} required
              onChange={(e) => setBookingForm({...bookingForm, visitDate: e.target.value})}
              min={new Date().toISOString().split('T')[0]}
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                border: '2px solid rgba(226, 232, 240, 0.6)', 
                borderRadius: '12px', 
                fontSize: '15px',
                fontWeight: '600',
                background: 'rgba(255,255,255,0.9)',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(226, 232, 240, 0.6)';
                e.target.style.boxShadow = 'none';
              }}
            />
            {selectedTicket?.category === 'pass_ticket' && (
              <div style={{ 
                fontSize: '12px', 
                color: '#10b981', 
                marginTop: '6px',
                fontWeight: '600',
                textAlign: 'center',
                background: 'rgba(16, 185, 129, 0.1)',
                padding: '4px 8px',
                borderRadius: '8px'
              }}>
                Valid for {selectedTicket?.passTicketSettings?.validityDays || 30} days
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Availability Check for Function Hall */}
        {selectedTicket?.category === 'function_hall_ticket' && bookingForm.visitDate && (
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '12px', color: '#0369a1', fontWeight: '600' }}>
              📅 Checking availability for {new Date(bookingForm.visitDate).toLocaleDateString()}...
            </div>
          </div>
        )}

        {/* Pass Pricing Options */}
        {selectedTicket?.category === 'pass_ticket' && passType && selectedTicket?.passTicketSettings?.passPricing && (
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>💰 Select Pass Duration</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {Object.entries(selectedTicket.passTicketSettings.passPricing)
                .filter(([_, config]) => config.price > 0)
                .map(([period, config]) => {
                  const labels = {
                    monthly: 'Monthly',
                    quarterly: 'Quarterly', 
                    halfYearly: 'Half Yearly',
                    yearly: 'Yearly'
                  };
                  const isSelected = categoryData.selectedPassPeriod === period;
                  return (
                    <div
                      key={period}
                      onClick={() => setCategoryData({...categoryData, selectedPassPeriod: period, passPeriodPrice: config.price, validityDays: config.validityDays})}
                      style={{
                        padding: '16px',
                        border: isSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: isSelected ? '#eff6ff' : 'white',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>{labels[period]}</div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{config.validityDays} days</div>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#3b82f6', marginTop: '8px' }}>₹{config.price}</div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
        
        {/* Slot Selection for Pass Tickets */}
        {selectedTicket?.category === 'pass_ticket' && selectedTicket?.passTicketSettings?.slotManagement?.enabled && (
          <div style={{
            background: '#fef7e0',
            border: '1px solid #f6cc02',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#92400e' }}>⏰ Select Time Slot</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {(slotAvailability.length > 0 ? slotAvailability : selectedTicket.passTicketSettings.slotManagement.timeSlots).map((slot, index) => {
                const isSelected = categoryData.selectedSlot === `${slot.startTime}-${slot.endTime}`;
                const available = slot.available !== undefined ? slot.available : (slot.maxCapacity - (slot.currentBookings || 0));
                return (
                  <div
                    key={index}
                    onClick={() => {
                      if (available > 0) {
                        setCategoryData({...categoryData, selectedSlot: `${slot.startTime}-${slot.endTime}`, slotStartTime: slot.startTime, slotEndTime: slot.endTime});
                      }
                    }}
                    style={{
                      padding: '16px',
                      border: isSelected ? '2px solid #f59e0b' : available > 0 ? '1px solid #e2e8f0' : '1px solid #ef4444',
                      borderRadius: '8px',
                      cursor: available > 0 ? 'pointer' : 'not-allowed',
                      background: isSelected ? '#fef3c7' : available > 0 ? 'white' : '#fef2f2',
                      opacity: available > 0 ? 1 : 0.6,
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>{slot.startTime} - {slot.endTime}</div>
                    <div style={{ fontSize: '12px', color: available > 0 ? '#059669' : '#dc2626', marginTop: '4px' }}>
                      {available > 0 ? `${available} spots available` : 'Fully booked'}
                    </div>
                    <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>Max: {slot.maxCapacity}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Pass Type Display */}
        {selectedTicket?.category === 'pass_ticket' && passType && (
          <div style={{
            background: passType === 'renew' ? '#f0f9ff' : '#f0fdf4',
            border: passType === 'renew' ? '1px solid #bae6fd' : '1px solid #bbf7d0',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: passType === 'renew' ? '#1d4ed8' : '#166534' }}>
              {passType === 'renew' ? '🔄 Renewing Existing Pass' : '🆕 New Pass Application'}
            </div>
            <div style={{ fontSize: '14px', color: passType === 'renew' ? '#0369a1' : '#15803d', marginTop: '4px' }}>
              {passType === 'renew' ? 'Extending validity with existing details' : `Creating new ${categoryData.selectedPassPeriod || 'monthly'} pass`}
            </div>
          </div>
        )}
        
        {/* Renewal Data Display */}
        {selectedTicket?.category === 'pass_ticket' && renewalData && isRenewal && (
          <div style={{
            background: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '14px', color: '#92400e', fontWeight: '600' }}>
              ℹ️ Found existing pass data - details will be auto-filled
            </div>
          </div>
        )}

        {/* Category-Specific Fields */}
        {['parking_ticket', 'chappal_stand_ticket', 'puja_ticket', 'function_hall_ticket', 'event_ticket', 'pass_ticket'].includes(selectedTicket?.category) && passType && (
          <CategorySpecificBooking 
            ticket={selectedTicket}
            onBookingDataChange={setCategoryData}
            visitDate={bookingForm.visitDate}
            initialData={renewalData && isRenewal ? {
              ...renewalData.additionalInfo,
              documents: {
                passportPhoto: renewalData.documents?.passportPhoto,
                idProof: renewalData.documents?.idProof
              }
            } : {}}
            passType={passType}
            isRenewal={isRenewal}
          />
        )}
        
        {/* Show category fields for non-pass tickets */}
        {['parking_ticket', 'chappal_stand_ticket', 'puja_ticket', 'function_hall_ticket', 'event_ticket'].includes(selectedTicket?.category) && (
          <CategorySpecificBooking 
            ticket={selectedTicket}
            onBookingDataChange={setCategoryData}
            visitDate={bookingForm.visitDate}
          />
        )}



        {/* Payment Gateway Section */}
        <div style={{
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid rgba(255,255,255,0.3)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
          position: 'relative',
          zIndex: 1
        }}>
          <h4 style={{
            margin: '0 0 16px 0',
            fontSize: '16px',
            fontWeight: '700',
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            💳 Payment Method
          </h4>
          <PaymentGatewaySelector onSelect={setSelectedGateway} selectedGateway={selectedGateway} />
        </div>

        {/* Total & Submit Section */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '20px',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z"/%3E%3C/g%3E%3C/svg%3E")',
            pointerEvents: 'none'
          }} />
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            gap: '20px',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ 
              background: 'rgba(255,255,255,0.2)', 
              backdropFilter: 'blur(10px)',
              color: 'white',
              padding: '16px 24px', 
              borderRadius: '16px', 
              textAlign: 'center', 
              minWidth: '140px',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              <div style={{ 
                fontSize: '13px', 
                opacity: 0.9,
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}>Total Amount</div>
              <div style={{ 
                fontSize: '2rem', 
                fontWeight: '900',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>₹{totalAmount}</div>
            </div>
            <button type="submit" disabled={!selectedGateway} style={{
              flex: 1, 
              padding: '16px 32px',
              background: selectedGateway ? 
                'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 
                'rgba(203, 213, 224, 0.8)',
              color: 'white', 
              border: 'none', 
              borderRadius: '16px',
              fontSize: '18px', 
              fontWeight: '800', 
              cursor: selectedGateway ? 'pointer' : 'not-allowed',
              boxShadow: selectedGateway ? 
                '0 10px 25px rgba(16, 185, 129, 0.4)' : 
                '0 4px 15px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              backdropFilter: 'blur(10px)',
              border: selectedGateway ? '2px solid rgba(255,255,255,0.2)' : '2px solid rgba(203, 213, 224, 0.3)'
            }}
            onMouseOver={(e) => {
              if (selectedGateway) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 15px 35px rgba(16, 185, 129, 0.5)';
              }
            }}
            onMouseOut={(e) => {
              if (selectedGateway) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 10px 25px rgba(16, 185, 129, 0.4)';
              }
            }}>
              {selectedGateway ? '🚀 Complete Payment' : '⚠️ Select Payment Method'}
            </button>
          </div>
        </div>
      </form>

      {/* PhonePe Payment Modal */}
      {showPhonePeModal && phonePeData && (
        <PhonePePaymentModalEnhanced
          checkoutUrl={phonePeData.checkoutUrl}
          orderId={phonePeData.orderId}
          bookingId={phonePeData.bookingId}
          onClose={() => {
            setShowPhonePeModal(false);
            setPhonePeData(null);
            // Optionally navigate back or refresh
            if (onBookingSubmit) {
              onBookingSubmit();
            }
          }}
        />
      )}

    </div>
  );
};

export default CompactBooking;